import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { IPurchasedProductsBySite } from "./Models";
import { getPurchasedProducts } from "./API";
import { LoadingState } from "../app/LoadingState";

interface IPurchasedProductsState {
  productsBySite: IPurchasedProductsBySite;
  isLoading: boolean;
  loadingState: LoadingState;
  activeRequestId?: string;
  loadingStateBeforeRequest?: LoadingState;
  error: string | null;
}

const initialState: IPurchasedProductsState = {
  productsBySite: {},
  isLoading: false,
  loadingState: LoadingState.None,
  error: null,
};

const purchasedProductsSlice = createSlice({
  name: "purchasedProducts",
  initialState,
  reducers: {
    updatePurchasedProducts: (state, action: PayloadAction<IPurchasedProductsBySite>) => {
      Object.assign(state.productsBySite, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPurchasedProducts.pending, (state, action) => {
        if (state.loadingState !== LoadingState.Pending) {
          state.loadingStateBeforeRequest = state.loadingState;
        }
        state.activeRequestId = action.meta.requestId;
        state.isLoading = true;
        state.loadingState = LoadingState.Pending;
        state.error = null;
      })
      .addCase(fetchPurchasedProducts.fulfilled, (state, action) => {
        if (state.activeRequestId !== action.meta.requestId) {
          return;
        }
        delete state.activeRequestId;
        state.isLoading = false;
        state.loadingState = LoadingState.Complete;
        state.error = null;
        state.productsBySite = action.payload;
      })
      .addCase(fetchPurchasedProducts.rejected, (state, action) => {
        if (state.activeRequestId !== action.meta.requestId) {
          return;
        }
        delete state.activeRequestId;
        state.isLoading = false;
        state.loadingState = action.meta.aborted
          ? (state.loadingStateBeforeRequest ?? LoadingState.None)
          : LoadingState.Error;
        state.error = action.meta.aborted ? null : (action.error.message ?? "Unknown error");
      });
  },
});

export const fetchPurchasedProducts = createAsyncThunk<IPurchasedProductsBySite, string[]>(
  "purchasedProducts/fetch",
  async (siteIds) => {
    return getPurchasedProducts(siteIds);
  }
);

export const purchasedProductsReducer = purchasedProductsSlice.reducer;
export const { updatePurchasedProducts } = purchasedProductsSlice.actions;
